import React from 'react';

// Dependency-free markdown renderer. No dangerouslySetInnerHTML — every
// piece of text is passed through React as a child, so raw HTML in the
// input (e.g. <script>) is escaped and shown literally, never executed.
// Links are http(s) or an in-app /portal/ path (Audrey points at the calculators).
// Underscore italics are deliberately unsupported: snake_case identifiers
// (contact_id, loan_amount) appear in answers and must stay literal.

const INLINE_REGEX = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/portal\/[^\s)]*)\))|(\*[^*]+\*)/g;

function parseInline(text, keyBase) {
  const nodes = [];
  let lastIndex = 0;
  let idx = 0;
  let m;
  INLINE_REGEX.lastIndex = 0;
  while ((m = INLINE_REGEX.exec(text))) {
    if (m.index > lastIndex) nodes.push(text.slice(lastIndex, m.index));
    const key = `${keyBase}-i${idx++}`;
    if (m[1]) {
      nodes.push(<code key={key} className="px-1 py-0.5 rounded bg-slate-100 text-[0.85em] font-mono">{m[1].slice(1, -1)}</code>);
    } else if (m[2]) {
      nodes.push(<strong key={key}>{m[2].slice(2, -2)}</strong>);
    } else if (m[3]) {
      nodes.push(
        <a key={key} href={m[5]} target="_blank" rel="noopener noreferrer" className="underline decoration-1 underline-offset-2">
          {m[4]}
        </a>
      );
    } else if (m[6]) {
      nodes.push(<em key={key}>{m[6].slice(1, -1)}</em>);
    }
    lastIndex = INLINE_REGEX.lastIndex;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

function Paragraph({ lines, keyBase }) {
  return <p key={keyBase} className="leading-relaxed">{parseInline(lines.join(' '), keyBase)}</p>;
}

export default function SimpleMarkdown({ text }) {
  if (!text) return null;
  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let blockKey = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (/^```/.test(line)) {
      const codeLines = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) { codeLines.push(lines[i]); i++; }
      i++; // skip closing fence (if present)
      blocks.push(
        <pre key={`b${blockKey++}`} className="bg-slate-100 rounded-lg p-3 my-1 overflow-x-auto">
          <code className="text-[0.85em] font-mono whitespace-pre">{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const key = `b${blockKey++}`;
      const content = parseInline(heading[2], key);
      const cls = level === 1 ? 'text-lg font-bold mt-2 mb-1' : level === 2 ? 'text-base font-bold mt-2 mb-1' : 'text-sm font-bold mt-1 mb-1';
      const Tag = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
      blocks.push(<Tag key={key} className={cls}>{content}</Tag>);
      i++;
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      const key = `b${blockKey++}`;
      blocks.push(
        <ul key={key} className="list-disc pl-5 space-y-0.5">
          {items.map((item, n) => <li key={`${key}-${n}`}>{parseInline(item, `${key}-${n}`)}</li>)}
        </ul>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      const key = `b${blockKey++}`;
      blocks.push(
        <ol key={key} className="list-decimal pl-5 space-y-0.5">
          {items.map((item, n) => <li key={`${key}-${n}`}>{parseInline(item, `${key}-${n}`)}</li>)}
        </ol>
      );
      continue;
    }

    if (line.trim() === '') { i++; continue; }

    const paraLines = [];
    while (
      i < lines.length && lines[i].trim() !== '' &&
      !/^```/.test(lines[i]) && !/^(#{1,3})\s+/.test(lines[i]) &&
      !/^[-*]\s+/.test(lines[i]) && !/^\d+\.\s+/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    const key = `b${blockKey++}`;
    blocks.push(<Paragraph key={key} lines={paraLines} keyBase={key} />);
  }

  return <div className="space-y-2">{blocks}</div>;
}
